import { useEffect } from 'react';

import { basicMsg } from '../../../../actions/swal_msg';
import { getOrdersByFilter } from '../../../../actions/orders';
import { formatMonth } from '../../../../helpers/admin/adminHelper';

import {
    Stack, FormControl, InputLabel, Select, MenuItem, Box,
    Chip, Button, Alert
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const ALL_MONTHS = [
    { label: "Ene", value: 1 },
    { label: "Feb", value: 2 },
    { label: "Mar", value: 3 },
    { label: "Abr", value: 4 },
    { label: "May", value: 5 },
    { label: "Jun", value: 6 },
    { label: "Jul", value: 7 },
    { label: "Ago", value: 8 },
    { label: "Sep", value: 9 },
    { label: "Oct", value: 10 },
    { label: "Nov", value: 11 },
    { label: "Dic", value: 12 }
];

const paymentStatuses = [
    { label: "Abandonado", value: "No realizado", color: "#F0F0F0" },
    { label: "Pendiente de pago", value: "Pendiente de pago", color: "#BBDEFB" },
    { label: "Cancelado", value: "Cancelado", color: "#FFE0B2" },
    { label: "Rechazado", value: "Rechazado", color: "#EF9A9A" },
    { label: "Devolución", value: "Devolución", color: "#E3F6E8" },
    { label: "Problema con pago", value: "Error con pago", color: "#FFF8DC" },
    { label: "Estatus desconocido", value: "Desconocido", color: "#B0BEC5" }
];

const PedidosFiltro = ({
    ordersCache, setOrdersCache, setData, currentDate,
    filter, setFilter, previousFilter, setPreviousFilter,
    setBackdrop, setOpen
}) => {
    const { years, currentYear, currentMonth } = currentDate;
    const { year, months, statuses } = filter;

    // estado de pago está seleccionado
    const statusSelected = paymentStatuses.length === statuses.length;

    useEffect(() => {
        const availableMonths = year === currentYear
            ? ALL_MONTHS.filter(e => e.value <= currentMonth)
            : ALL_MONTHS;
        const availableValues = availableMonths.map(({ value }) => value);

        setFilter(current => {
            const validMonths = current.months.filter(month =>
                availableValues.includes(month)
            );
            return validMonths.length === current.months.length ?
                current : { ...current, months: validMonths };
        });
    }, [year, currentYear, currentMonth, setFilter]);

    const filteredMonths = year === currentYear
        ? ALL_MONTHS.filter(e => e.value <= currentMonth)
        : ALL_MONTHS;

    const areArraysEqual = (a, b) => {
        if (a.length !== b.length) return false;

        const setA = new Set(a);
        const setB = new Set(b);

        if (setA.size !== setB.size) return false;

        for (const value of setA) {
            if (!setB.has(value)) return false;
        };

        return true;
    };

    const handleChange = ({ target: { name, value } }) => {
        setFilter(e => ({ ...e, [name]: value }));
    };

    const handleToggle = (monthValue) => {
        setFilter(e => ({
            ...e,
            months: e.months.includes(monthValue)
                ? e.months.filter(u => u !== monthValue)
                : [...e.months, monthValue]
        }));
    };

    // selección estado del pago
    const handleStatusToggle = (statusValue) => {
        setFilter(e => ({
            ...e,
            statuses: e.statuses.includes(statusValue)
                ? e.statuses.filter(u => u !== statusValue)
                : [...e.statuses, statusValue]
        }));
    };

    const hasPendingChanges = Boolean(year) && (
        year !== previousFilter.year ||
        !areArraysEqual(months, previousFilter.months) ||
        !areArraysEqual(statuses, previousFilter.statuses)
    );

    const applyFiters = async () => {
        if (
            year === previousFilter.year &&
            areArraysEqual(months, previousFilter.months) &&
            areArraysEqual(statuses, previousFilter.statuses)
        ) {
            basicMsg('Filtro ya aplicado');
            return;
        }
        
        // filtrar meses donde no hay información en caché
        const monthsToFetch = months.filter(month => {
            const key = `${year}-${formatMonth(month)}`;
            return !ordersCache[key];
        });

        if (monthsToFetch.length <= 0) {
            // ya se tiene la información, solo actualizar la tabla
            setPreviousFilter(e => ({
                ...e,  year, months, statuses
            }));
            setData(
                months.flatMap(month => {
                    const key = `${year}-${formatMonth(month)}`;
                    const array = ordersCache[key] || [];
                    return array.filter(e => (
                        statuses.includes(e.payment_status) ||
                        (
                            statuses.includes("Error con pago")
                            && e.payment.has_errors
                        )
                    ));
                }
            ));
            return;
        }

        setBackdrop(true);
        await getOrdersByFilter(year, monthsToFetch, setOrdersCache, setBackdrop);
    };

    // regresar a último filtro aplicado
    const resetFilters = () => {
        setFilter(e => ({
            ...e, months: [], statuses: []
        }));
    };

    return (
        <>
            <Stack direction="row" flexWrap="wrap" alignItems="flex-start" sx={{ gap: 2 }}>
                <FormControl size="small" sx={{ width: "auto", minWidth: 100 }}>
                <InputLabel id="year-select-label">Año</InputLabel>
                <Select
                    labelId="year-select-label"
                    id="year-simple-select"
                    name="year"
                    value={year}
                    label="Año"
                    onChange={handleChange}
                >
                    {
                        years.map((e, i) =>
                            <MenuItem key={i} value={e}>{e}</MenuItem>
                        )
                    }
                </Select>
                </FormControl>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Chip
                    label="Todos"
                    clickable
                    color={months.length === filteredMonths.length ? "primary" : "default"}
                    onClick={() =>
                        setFilter(e => ({
                            ...e,
                            months: e.months.length === filteredMonths.length
                            ? []
                            : filteredMonths.map(u => u.value)
                        }))
                    }
                />

                {filteredMonths.map(month => {
                    const selected = months.includes(month.value);
                    return (
                        <Chip
                            key={month.value}
                            label={month.label}
                            clickable
                            onClick={() => handleToggle(month.value)}
                            color={selected ? "primary" : "default"}
                            variant={selected ? "filled" : "outlined"}
                        />
                    );
                })}
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Chip
                    label="Todos"
                    clickable
                    onClick={() =>
                        setFilter(e => ({
                            ...e,
                            statuses: e.statuses.length === paymentStatuses.length
                            ? []
                            : paymentStatuses.map(u => u.value)
                        }))
                    }
                    size="small"
                    sx={{
                        border: `1px solid gray`,
                        backgroundColor: statusSelected ? "primary.main" : "transparent",
                        color: statusSelected ? "primary.contrastText" : "text.primary",
                        fontWeight: 500,
                        transition: "all 0.2s ease"
                    }}
                />

                {paymentStatuses.map(e => {
                    const selected = statuses.includes(e.value);

                    return (
                        <Chip
                            key={e.value}
                            label={e.label}
                            clickable
                            onClick={() => handleStatusToggle(e.value)}
                            size="small"
                            sx={{
                                border: `1px solid ${e.color}`,
                                backgroundColor: selected ? e.color : "transparent",
                                color: selected ? "#000" : "text.primary",
                                fontWeight: 500,
                                transition: "all 0.2s ease"
                            }}
                        />
                    );
                })}
                </Box>

                <Button
                    size="small"
                    variant="contained"
                    color={hasPendingChanges ? 'warning' : 'primary'}
                    onClick={applyFiters}
                >
                    Aplicar
                </Button>
                <Button size="small" variant="outlined" onClick={resetFilters}>Limpiar</Button>
                <Button size="small" variant="contained" endIcon={<FileDownloadIcon />}
                    onClick={ () => setOpen(true) }>
                    Exportar
                </Button>
            </Stack>

            {hasPendingChanges && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Últimos cambios no aplicados. Presiona Aplicar para actualizar los pedidos.
                </Alert>
            )}
        </>
    );
};

export default PedidosFiltro;
